import {
  Box,
  Button,
  classSet,
  Modal
} from '@platform/ui-library';
import {
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef
} from 'react';
import styles from './confirmation-modal.module.scss';

export type ConfirmationModalProps = PropsWithChildren<{
  /** Hides the cancel button. */
  hideCancel?: boolean;
  /** Hides the continue button. */
  hideContinue?: boolean;
  /** Whether to display the modal @default true. */
  isOpen?: boolean;
  /** Labels used in the component. */
  labels?: {
    cancelButton?: string;
    continueButton?: string;
    /** Header with details about what is being confirmed. */
    header?: ReactNode;
    /** Smaller header with more details than @see header. */
    subheader?: ReactNode;
  };
  /** Called when user makes a choice to proceed or not. */
  onComplete: (proceed: boolean) => void;
}>;

export const ConfirmationModal: FC<ConfirmationModalProps> = ({
  children = window.l.popup.wishToProceed,
  hideCancel,
  hideContinue,
  isOpen = true,
  labels: {
    cancelButton = window.l.popup.cancel,
    continueButton = window.l.popup.continue,
    header = 'Confirm',
    subheader
  } = {},
  onComplete
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const button = contentRef?.current?.querySelectorAll('button');
    [...(button ?? [])].at(-1)?.focus();
  }, []);

  return (
    <Modal isOpen={isOpen}>
      <Box theme="noPadding">
        <div className={classSet(['box flex-column header', (children == null) ? 'no-border' : ''])}>
          <h2>{header}</h2>
          {(!subheader) ? null : (
            <p>{subheader}</p>
          )}
        </div>
        {(typeof children !== 'string') ? children : (
          <p className={`box content ${styles.textContent}`}>{children}</p>
        )}
        <div className="box footer" ref={contentRef}>
          {(hideCancel) ? null : (
            <Button
              onClick={() => {
                onComplete(false);
              }}
              theme="outline"
              type="button"
            >
              {cancelButton}
            </Button>
          )}
          {(hideContinue) ? null : (
            <Button
              onClick={async () => {
                onComplete(true);
              }}
              theme="default"
              type="button"
            >
              {continueButton}
            </Button>
          )}
        </div>
      </Box>
    </Modal>
  );
};
